// @ts-nocheck
import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
    this.addEventListener('focusout', (focustOutEvent) => {
      const next = focustOutEvent.relatedTarget;
      if (!this.contains(next)) {
        this.setAttribute('aria-expanded', false);
        this.setAttribute('data-overflow-expanded', false);
        this.style.setProperty('--submenu-height', '0px');
        this.style.setProperty('--submenu-opacity', '0');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {Array}
   */
  #activeListeners = [];

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const targetItem = event.target.tagName.toLowerCase() === 'li' ? event.target : event.target.closest('li');
    let item = findMenuItem(targetItem);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.dataset.active = 'false';

      const previousActiveButton = previouslyActiveItem.nextElementSibling;
      if (previousActiveButton && previousActiveButton.tagName.toLocaleLowerCase() === 'button' && previousActiveButton.hasAttribute('aria-expanded')) {
        previousActiveButton.ariaExpanded = 'false';
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';

    item.dataset.active = true;

    const activeButton = item.nextElementSibling;
    if (activeButton && activeButton.tagName.toLocaleLowerCase() === 'button' && activeButton.hasAttribute('aria-expanded')) {
      activeButton.ariaExpanded = 'true';
    }

    let submenu = findSubmenu(targetItem);

    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   *
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;
    const targetItem = event.target.tagName.toLowerCase() === 'li' ? event.target : event.target.closest('li');

    const item = findMenuItem(targetItem);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.dataset.active = 'false';
    const activeButton = item.nextElementSibling;
    if (activeButton && activeButton.tagName.toLocaleLowerCase() === 'button' && activeButton.hasAttribute('aria-expanded')) {
      activeButton.ariaExpanded = 'false';
    }

    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Activate the selected submenu item with a delay
   * @param {PointerEvent} event
   */

  showSubmenu(event) {
    if (!this.#activeListeners.includes(event.target)) {
      let parent = null;
      let targetElement = null;
      let submenu = null;
      event.target?.addEventListener('keydown', (keyEvent) => {
        keyEvent.stopPropagation();
        if (keyEvent.code === 'Enter') {
          const subMenuId = keyEvent.target.getAttribute('aria-controls');
          parent = keyEvent.target.closest('[data-menu-item]');
          submenu = this.querySelector(`#${subMenuId}`);
          parent.slot === 'overflow'
            ? this.toggleOverflowItemHandler(event.target, submenu)
            : event.target?.getAttribute('aria-expanded') === 'false'
            ? this.openSubMenuHandler(event, submenu)
            : this.deactivate(event);
        }
        if (keyEvent.code === 'Escape') {
          parent = keyEvent.target.closest('[data-menu-item]');
          parent.slot === 'overflow' ? this.hideOverflowSubmenu(event, keyEvent, submenu) : this.deactivate(event);
        }
        if (submenu) {
          submenu.addEventListener('keydown', (e) => {
            if (e.code === 'Escape') {
              event.target.focus();
            }
          });
        }
      });
      this.#activeListeners.push(event.target);
    }
  }
  /**
   * Activate the selected submenu item with a delay
   * @param {PointerEvent} event
   * @param {HTMLElement | null} [submenu]
   */

  openSubMenuHandler(event, submenu) {
    this.activate(event);
    if (!submenu) return;

    submenu.addEventListener('focusout', (focustOutEvent) => {
      const next = focustOutEvent.relatedTarget;

      if (!submenu.contains(next)) {
        this.deactivate(event);
      }
    });
  }
  /**
   * @param {HTMLElement | null} [targetElement]
   * @param {HTMLElement | null} [submenu]
   *
   */
  toggleOverflowItemHandler(targetElement, submenu) {
    targetElement.getAttribute('aria-expanded') === 'false'
      ? (targetElement.setAttribute('aria-expanded', true), submenu?.classList.add('visible'))
      : (targetElement.setAttribute('aria-expanded', false), submenu?.classList.remove('visible'));

    submenu.addEventListener('focusout', (focustOutEvent) => {
      const next = focustOutEvent.relatedTarget;
      if (!submenu.contains(next)) {
        targetElement.setAttribute('aria-expanded', false);
        submenu?.classList.remove('visible');
      }
    });
  }

  toggleOverflowMenu(event) {
    if (!this.#activeListeners.includes(event.target)) {
      event.target?.addEventListener('keydown', (keyEvent) => {
        if (keyEvent.code === 'Enter') {
          event.target.getAttribute('aria-expanded') === 'false' ? this.activateOverflow(event.target) : this.hideOverflow(event.target);
        } else if (keyEvent.code === 'Escape') {
          this.hideOverflow(event.target);
        } else if (keyEvent.code === 'Tab') {
          if (keyEvent.shiftKey) {
            this.hideOverflow(event.target);
          }
        }
        if (this.overflowMenu) {
          this.overflowMenu.addEventListener('keydown', (keyEvent) => {
            if (keyEvent.code === 'Escape') {
              this.hideOverflow(event.target);
              const moreButton = document.querySelector('li[slot="more"] button');
              if (moreButton) {
                moreButton.focus();
              }
            }
          });
        }
      });
    }
    this.#activeListeners.push(event.target);
  }

  activateOverflow(target) {
    let submenu = null;
    const isDefaultSlot = target.slot === '';
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    this.setAttribute('aria-expanded', true);
    target.setAttribute('aria-expanded', true);
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
  }

  hideOverflow(target) {
    target.setAttribute('aria-expanded', false);
    this.ariaExpanded = 'false';
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
  }

  hideOverflowSubmenu(event, keyEvent, submenu) {
    if (keyEvent.target?.getAttribute('aria-expanded') === 'false') {
      const moreButton = document.querySelector('li[slot="more"] button');
      this.hideOverflow(moreButton.parentElement);
      moreButton.focus();
    }

    keyEvent.target?.setAttribute('aria-expanded', false);
    submenu?.classList.remove('visible');
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('a');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
